import {
  AccountBrand,
  AccountConfig,
  DynamicPage,
  DynamicPageVariant,
  DynamicPageVariantWithId,
  DynamicPageVersion,
  DynamicPageVersionDiff,
  Website,
  WebsiteData,
  WithId,
} from '@betterfunnel/types';
import { diff } from 'deep-diff';
import {
  collection,
  documentId,
  DocumentSnapshot,
  getDoc,
  getDocs,
  query,
  QueryDocumentSnapshot,
  Timestamp,
  where,
} from 'firebase/firestore';
import { capitalize, chunk, distinct, emptyConverter } from '.';
import { getAccountBrandDocRef } from '../services/AccountBrandService';
import { getAccountConfigDocRef } from '../services/AccountConfigService';
import { getDynamicPageVariantsQuery } from '../services/DynamicPageVariantsService';

export const createPreviewDynamicPageVersion = async (
  dynamicPage: DocumentSnapshot<DynamicPage>,
  createdBy: string,
): Promise<DynamicPageVersion> => {
  const dynamicPageData = dynamicPage.data() as DynamicPage;
  if (!dynamicPage.exists() || !dynamicPageData) {
    throw new Error('Dynamic page does not exist');
  }

  const accountDocRef = dynamicPage.ref.parent.parent;
  const accountId = accountDocRef?.id;
  if (!accountDocRef || !accountId) {
    throw new Error('Dynamic page does not have an account');
  }

  const variantsDocs = await getDocs(
    query(
      getDynamicPageVariantsQuery(accountId, dynamicPage.id),
      where('status', '==', 'active'),
    ),
  ).then((snapshot) => snapshot.docs);

  if (!variantsDocs.length) {
    throw new Error('Dynamic page does not have any variants');
  }

  const variantsWithIds: DynamicPageVariantWithId[] = variantsDocs.map(
    (doc) => ({
      id: doc.id,
      ...doc.data(),
    }),
  );

  const websitesIds = distinct(variantsWithIds.flatMap((v) => v.websiteIds));
  const allWebsitesDocs: QueryDocumentSnapshot<Website>[] = [];

  // Process websitesIds in batches of 30
  for (const websitesIdsChunk of chunk(websitesIds, 30)) {
    const websitesBatchDocs = await getDocs(
      query(
        collection(dynamicPage.ref, 'websites').withConverter(
          emptyConverter<Website>(),
        ),
        where(documentId(), 'in', websitesIdsChunk),
      ),
    );

    allWebsitesDocs.push(...websitesBatchDocs.docs);
  }

  const issues: string[] = [];

  const websitesMap = new Map(allWebsitesDocs.map((doc) => [doc.id, doc]));
  variantsWithIds.forEach((variant) => {
    // clean up websiteIds
    variant.websiteIds = variant.websiteIds.filter((websiteId) => {
      if (!websitesMap.has(websiteId)) {
        return false;
      }
      return true;
    });

    if (variant.websiteIds.length === 0) {
      issues.push(`Variant ${variant.name} has no active websites`);
    }
  });

  if (issues.length) {
    throw new Error(issues.join('\n'));
  }

  const accountConfigDoc = await getDoc(getAccountConfigDocRef(accountId));
  const accountBrandDoc = await getDoc(getAccountBrandDocRef(accountId));

  return {
    name: '',
    description: '',
    type: 'dynamicPageVersion',
    createdBy,
    createdAt: Timestamp.now(),
    dynamicPage: { ...dynamicPageData, id: dynamicPage.id },
    variants: variantsWithIds,
    accountConfig: accountConfigDoc.data(),
    accountBrand: accountBrandDoc.data(),
    websites: allWebsitesDocs.map((doc) => ({
      id: doc.id,
      ...(doc.data().production ?? doc.data().development),
    })),
  };
};

export const diffDynamicPageVersions = (
  oldVersion: DynamicPageVersion,
  newVersion: DynamicPageVersion,
): DynamicPageVersionDiff[] => {
  const dynamicPagesDiff = getDynamicPagesDiff(
    oldVersion.dynamicPage,
    newVersion.dynamicPage,
  );

  const variantsDiff = getVariantsDiff(
    oldVersion.variants,
    newVersion.variants,
  );

  const websitesDiff = getWebsitesDiff(
    oldVersion.websites,
    newVersion.websites,
  );

  const dynamicPageConfigDiff = getDynamicPageConfigDiff(
    oldVersion.dynamicPage.config || {},
    newVersion.dynamicPage.config || {},
  );

  const dynamicPageBrandDiff = getDynamicPageBrandDiff(
    oldVersion.dynamicPage.brand || {},
    newVersion.dynamicPage.brand || {},
  );

  const accountConfigDiff = getAccountConfigDiff(
    oldVersion.accountConfig || {},
    newVersion.accountConfig || {},
  );

  const accountBrandDiff = getAccountBrandDiff(
    oldVersion.accountBrand || {},
    newVersion.accountBrand || {},
  );

  return [
    ...dynamicPagesDiff,
    ...variantsDiff,
    ...websitesDiff,
    ...dynamicPageConfigDiff,
    ...dynamicPageBrandDiff,
    ...accountConfigDiff,
    ...accountBrandDiff,
  ];
};

const getEntityDiff = function <T>(
  oldEntities: T[] = [],
  newEntities: T[] = [],
  entityId: (entity: T) => string,
): { added: T[]; removed: T[]; changed: { old: T; new: T }[] } {
  const oldEntitiesMap = new Map(
    oldEntities.map((entity) => [entityId(entity), entity]),
  );
  const newEntitiesMap = new Map(
    newEntities.map((entity) => [entityId(entity), entity]),
  );

  const added: T[] = [];

  newEntities.forEach((newEntity) => {
    if (!oldEntitiesMap.has(entityId(newEntity))) {
      added.push(newEntity);
    }
  });

  const removed: T[] = [];

  oldEntities.forEach((oldEntity) => {
    if (!newEntitiesMap.has(entityId(oldEntity))) {
      removed.push(oldEntity);
    }
  });

  const changed: { old: T; new: T }[] = [];

  newEntities.forEach((entity) => {
    const oldEntity = oldEntitiesMap.get(entityId(entity));
    if (!oldEntity) return;

    if (JSON.stringify(oldEntity) !== JSON.stringify(entity)) {
      changed.push({ new: entity, old: oldEntity });
    }
  });

  return { added, removed, changed };
};

const getDynamicPagesDiff = (
  oldDynamicPage: WithId<DynamicPage>,
  newDynamicPage: WithId<DynamicPage>,
): DynamicPageVersionDiff[] => {
  const metadataFields: (keyof DynamicPage)[] = [
    'name',
    'description',
    'goal',
    'tags',
  ];

  const partialOld = Object.fromEntries(
    Object.entries(oldDynamicPage).filter(([key]) =>
      metadataFields.includes(key as keyof DynamicPage),
    ),
  );
  const partialNew = Object.fromEntries(
    Object.entries(newDynamicPage).filter(([key]) =>
      metadataFields.includes(key as keyof DynamicPage),
    ),
  );

  const diffs: DynamicPageVersionDiff[] = [];

  const entitiesDiff = diff(partialOld, partialNew);
  if (entitiesDiff?.length) {
    diffs.push({
      type: 'changed',
      entity: 'dynamicPage',
      explanation: 'Dynamic Page changed',
      id: newDynamicPage.id,
      name: newDynamicPage.name,
      diff: entitiesDiff,
    });
  }

  const pathDiff = diff(oldDynamicPage.path, newDynamicPage.path);
  if (pathDiff?.length) {
    diffs.push({
      type: 'changed',
      entity: 'dynamicPage',
      explanation: `Dynamic Page path changed to /"${newDynamicPage.path}".`,
      id: newDynamicPage.id,
      name: newDynamicPage.name,
      diff: pathDiff,
    });
  }

  const { added: addedAlternatePathes, removed: removedAlternatePathes } =
    getEntityDiff(
      oldDynamicPage.alternatePathes,
      newDynamicPage.alternatePathes,
      (path) => path,
    );
  addedAlternatePathes.forEach((path) => {
    diffs.push({
      type: 'added',
      entity: 'dynamicPageAlternatePath',
      explanation: `Added alternate path /"${path}".`,
      id: newDynamicPage.id,
      name: newDynamicPage.name,
    });
  });

  removedAlternatePathes.forEach((path) => {
    diffs.push({
      type: 'removed',
      entity: 'dynamicPageAlternatePath',
      explanation: `Removed alternate path /"${path}".`,
      id: newDynamicPage.id,
      name: newDynamicPage.name,
    });
  });

  const { added: addedDomains, removed: removedDomains } = getEntityDiff(
    oldDynamicPage.domains,
    newDynamicPage.domains,
    (d) => d,
  );
  addedDomains.forEach((domain) => {
    diffs.push({
      type: 'added',
      entity: 'domain',
      explanation: `Added domain "${domain}".`,
      id: domain,
      name: domain,
    });
  });

  removedDomains.forEach((domain) => {
    diffs.push({
      type: 'removed',
      entity: 'domain',
      explanation: `Removed domain "${domain}".`,
      id: domain,
      name: domain,
    });
  });

  return diffs;
};

const getVariantsDiff = (
  oldVariants: WithId<DynamicPageVariant>[],
  newVariants: WithId<DynamicPageVariant>[],
): DynamicPageVersionDiff[] => {
  const diffs: DynamicPageVersionDiff[] = [];
  const { added, removed, changed } = getEntityDiff(
    oldVariants,
    newVariants,
    (variant) => variant.id,
  );

  changed.forEach(({ old: oldEntity, new: newEntity }) => {
    if (oldEntity.status !== newEntity.status) {
      diffs.push({
        type: 'changed',
        entity: 'variant',
        explanation: `Variant ${newEntity.name} status changed to ${capitalize(newEntity.status)}`,
        id: newEntity.id,
        name: newEntity.name,
        diff: [
          {
            kind: 'E',
            path: ['status'],
            lhs: oldEntity.status,
            rhs: newEntity.status,
          },
        ],
      });

      return;
    }

    const { added, removed } = getEntityDiff(
      oldEntity.websiteIds,
      newEntity.websiteIds,
      (websiteId) => websiteId,
    );

    if (added.length) {
      diffs.push({
        type: 'added',
        entity: 'variant',
        explanation: `New experiment added to: "${newEntity.name}".`,
        id: newEntity.id,
        name: newEntity.name,
      });
    }

    if (removed.length) {
      diffs.push({
        type: 'removed',
        entity: 'variant',
        explanation: `Experiment removed from: "${newEntity.name}".`,
        id: newEntity.id,
        name: newEntity.name,
      });
    }

    const nameDiff = diff(oldEntity.name, newEntity.name);
    if (nameDiff?.length) {
      diffs.push({
        type: 'changed',
        entity: 'variant',
        explanation: `Name changed from "${oldEntity.name}" to "${newEntity.name}".`,
        id: newEntity.id,
        name: newEntity.name,
        diff: nameDiff,
      });
    }

    const descriptionDiff = diff(oldEntity.description, newEntity.description);
    if (descriptionDiff?.length) {
      diffs.push({
        type: 'changed',
        entity: 'variant',
        explanation: `Description changed from "${oldEntity.description}" to "${newEntity.description}".`,
        id: newEntity.id,
        name: newEntity.name,
        diff: descriptionDiff,
      });
    }

    const entitiesDiff = diff(oldEntity.constraints, newEntity.constraints);
    if (entitiesDiff?.length) {
      diffs.push({
        type: 'changed',
        entity: 'variant',
        explanation: `Constraints changed in "${newEntity.name}".`,
        id: newEntity.id,
        name: newEntity.name,
        diff: entitiesDiff,
      });
    }
  });

  return [
    ...diffs,
    ...added.map(
      (variant) =>
        ({
          type: 'added',
          entity: 'variant',
          explanation: `Added variant "${variant.name}".`,
          id: variant.id,
          name: variant.name,
        }) satisfies DynamicPageVersionDiff,
    ),
    ...removed.map(
      (variant) =>
        ({
          type: 'removed',
          entity: 'variant',
          explanation: `Removed variant "${variant.name}".`,
          id: variant.id,
          name: variant.name,
        }) satisfies DynamicPageVersionDiff,
    ),
  ];
};

const getWebsitesDiff = (
  oldWebsites: WithId<WebsiteData>[],
  newWebsites: WithId<WebsiteData>[],
): DynamicPageVersionDiff[] => {
  const diffs: DynamicPageVersionDiff[] = [];
  const { changed } = getEntityDiff(
    oldWebsites,
    newWebsites,
    (website) => website.id,
  );

  changed.forEach(({ old: oldEntity, new: newEntity }) => {
    const entitiesDiff = diff(oldEntity, newEntity);
    if (entitiesDiff?.length) {
      diffs.push({
        type: 'changed',
        entity: 'website',
        explanation: 'Page content changed',
        id: newEntity.id,
        name: 'Page',
        diff: entitiesDiff,
      });
    }
  });

  return diffs;
};

const getAccountConfigDiff = (
  oldConfig: AccountConfig,
  newConfig: AccountConfig,
): DynamicPageVersionDiff[] => {
  const diffs: DynamicPageVersionDiff[] = [];

  const entitiesDiff = diff(oldConfig, newConfig);
  if (entitiesDiff?.length) {
    diffs.push({
      type: 'changed',
      entity: 'accountConfig',
      explanation: 'Account config changed',
      id: 'default',
      name: 'Account Config',
      diff: entitiesDiff,
    });
  }

  return diffs;
};

const getAccountBrandDiff = (
  oldBrand: AccountBrand,
  newBrand: AccountBrand,
): DynamicPageVersionDiff[] => {
  const diffs: DynamicPageVersionDiff[] = [];

  const entitiesDiff = diff(oldBrand, newBrand);
  if (entitiesDiff?.length) {
    diffs.push({
      type: 'changed',
      entity: 'accountBrand',
      explanation: 'Account brand changed',
      id: 'default',
      name: 'Account Brand',
      diff: entitiesDiff,
    });
  }

  return diffs;
};

const getDynamicPageConfigDiff = (
  oldConfig: AccountConfig,
  newConfig: AccountConfig,
): DynamicPageVersionDiff[] => {
  const diffs: DynamicPageVersionDiff[] = [];

  const entitiesDiff = diff(oldConfig, newConfig);
  if (entitiesDiff?.length) {
    diffs.push({
      type: 'changed',
      entity: 'dynamicPageConfig',
      explanation: 'Dynamic Page config changed',
      id: 'default',
      name: 'Dynamic Page Config',
      diff: entitiesDiff,
    });
  }

  return diffs;
};

const getDynamicPageBrandDiff = (
  oldBrand: AccountBrand,
  newBrand: AccountBrand,
): DynamicPageVersionDiff[] => {
  const diffs: DynamicPageVersionDiff[] = [];

  const entitiesDiff = diff(oldBrand, newBrand);
  if (entitiesDiff?.length) {
    diffs.push({
      type: 'changed',
      entity: 'dynamicPageBrand',
      explanation: 'Dynamic Page brand changed',
      id: 'default',
      name: 'Dynamic Page Brand',
      diff: entitiesDiff,
    });
  }

  return diffs;
};
